/* eslint-disable */
import {
  Grid,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Autocomplete,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  IconButton,
  Button
} from '@material-ui/core';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from 'prop-types';
import React from 'react';
import { isStringNumeric } from '../../../utils/formatNumber';

VMNetworkSettings.propTypes = {
  currentVM: PropTypes.object,
  updateCurrentVM: PropTypes.func
};

const minVLANTag = 1;
const maxVLANTag = 4094;

export function VMNetworkSettings({ currentVM, updateCurrentVM }) {
  // remove the interface from the list of interfaces
  const handleRemoveNetworkInterface = (interfaceIndex) => {
    const newVM = { ...currentVM, network: { ...currentVM.network } };
    newVM.network.ports = newVM.network.ports.filter((_, index) => index !== interfaceIndex);
    updateCurrentVM(newVM);
  };

  // add a new interface from the list of interfaces
  const handleAddNetworkInterface = () => {
    const newVM = { ...currentVM };

    // next is the max port number currently on the list, if there none just set next to 1
    const next =
      newVM.network.ports?.length > 0
        ? Math.max(...newVM.network.ports.map((i) => Number(i.name.replace('vm-p', '')))) + 1
        : 1;
    newVM.network.ports.push({ name: `vm-p${next}` });
    updateCurrentVM(newVM);
  };

  const handleVlanModeChange = (event, portIndex) => {
    const newVM = { ...currentVM };

    newVM.network.ports[portIndex].vlan_mode = event.target.value;
    if (event.target.value === '') {
      newVM.network.ports[portIndex] = { name: newVM.network.ports[portIndex].name };
    }

    updateCurrentVM(newVM);
  };

  const handleVlanTagChange = (event, portIndex) => {
    const vlan = event.target.value;

    if (vlan === '') {
      const newVM = { ...currentVM };
      newVM.network.ports[portIndex].tag = vlan;
      updateCurrentVM(newVM);
      return;
    }

    if (!isStringNumeric(vlan)) return;

    const vlanNumber = Number(vlan);
    if (vlanNumber < minVLANTag || vlanNumber > maxVLANTag) return;

    const newVM = { ...currentVM };
    newVM.network.ports[portIndex].tag = vlanNumber;
    updateCurrentVM(newVM);
  };

  const handleVlanTrunksChange = (trunks, portIndex) => {
    const newVM = { ...currentVM };
    const integerTrunks = trunks
      .map((trunk) => Number.parseInt(trunk, 10))
      .filter((trunk) => Number.isInteger(trunk) && trunk >= minVLANTag && trunk <= maxVLANTag);
    newVM.network.ports[portIndex].trunks = integerTrunks;
    updateCurrentVM(newVM);
  };

  return (
    <Grid item xs={12}>
      <Accordion>
        <AccordionSummary sx={{ paddingLeft: 0 }} expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Network Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={2} data-testid="vm-network-settings">
            <Typography value="Network Interfaces" variant="overline">
              Network Interfaces
            </Typography>
            {currentVM.network.ports?.map((netInterface, index) => (
              <Grid container item xs={12} columnSpacing={2} key={netInterface.name || index}>
                <Grid item xs={3}>
                  <TextField label="Port Name" disabled value={netInterface.name} size="small" />
                </Grid>
                <Grid item xs={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="vlan-mode-label">Vlan Mode</InputLabel>
                    <Select
                      labelId="vlan-mode-label"
                      data-testid="vlan-mode"
                      label="Vlan Mode"
                      fullWidth
                      size="small"
                      onChange={(event) => {
                        handleVlanModeChange(event, index);
                      }}
                      value={netInterface.vlan_mode ?? ''}
                    >
                      <MenuItem value="">Unset</MenuItem>
                      <MenuItem value="trunk">Trunk</MenuItem>
                      <MenuItem value="native-tagged">Native-Tagged</MenuItem>
                      <MenuItem value="native-untagged">Native-Untagged</MenuItem>
                      <MenuItem value="dot1q-tunnel">Dot1q-tunnel</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    inputProps={{ 'data-testid': 'vlan-field' }}
                    disabled={netInterface.vlan_mode === undefined || netInterface.vlan_mode === ''}
                    label="Vlan Tag"
                    size="small"
                    helperText="Allows [1,4094]"
                    onChange={(event) => {
                      handleVlanTagChange(event, index);
                    }}
                    value={currentVM.network.ports[index].tag ?? ''}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Autocomplete
                    disabled={netInterface.vlan_mode === undefined || netInterface.vlan_mode === ''}
                    multiple
                    options={[]}
                    freeSolo
                    onChange={(_, trunks) => {
                      handleVlanTrunksChange(trunks, index);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vlan Trunks"
                        size="small"
                        inputProps={{
                          ...params.inputProps,
                          'data-testid': 'trunk-field',
                          onBeforeInput: (event) => {
                            const nextValue = event.data;
                            if (nextValue && !isStringNumeric(nextValue)) {
                              event.preventDefault();
                            }
                          }
                        }}
                      />
                    )}
                    value={netInterface.trunks ? netInterface.trunks.map(String) : []}
                  />
                </Grid>
                {currentVM.network.ports.length > 1 && (
                  <Grid item xs={1}>
                    <IconButton
                      data-testid="remove-interface-btn"
                      color="error"
                      onClick={() => handleRemoveNetworkInterface(index)}
                    >
                      <DeleteOutlineOutlinedIcon />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            ))}
            <Button
              data-testid="add-network-interface-btn"
              startIcon={<AddIcon />}
              variant="contained"
              sx={{ maxWidth: 220 }}
              onClick={() => handleAddNetworkInterface()}
            >
              Add Network Interface
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
}
